import { useEffect, useMemo, useRef, useState } from 'react'
import { ambientSignalBus } from '../services/ai/ambient/ambientSignalBus'
import { evaluateAmbientOpportunities } from '../services/ai/ambient/ambientEvaluator'
import { refineOpportunitiesWithLLM, type AmbientLLMRefineDeps } from '../services/ai/ambient/ambientEvaluatorLLM'
import { getAmbientDismissals, getAmbientMutes, recordAmbientDismissal } from '../services/ai/ambient/ambientDismissalStore'
import { lineFeatureFlags } from '../config/lineFeatureFlags'
import type { AmbientOpportunity, AmbientEvaluatorInput } from '../services/ai/ambient/ambientTypes'

const EVAL_DEBOUNCE_MS = 400
const MIN_EVAL_INTERVAL_MS = 800

export interface UseAmbientOpportunityInput {
  workspaceId: string
  workspaceElements: AmbientEvaluatorInput['workspaceElements']
  checklistCompletionByOwner?: AmbientEvaluatorInput['checklistCompletionByOwner']
  uploadsWithoutTimelineRef?: AmbientEvaluatorInput['uploadsWithoutTimelineRef']
  /** Suppress evaluation entirely (Line is thinking, composer has text, focus noInterrupt). */
  suppressed: boolean
  /** Milliseconds since the last user keystroke; used by the LLM gate. */
  msSinceLastKeystroke: number
  /** GamificationContext.ledgerCredits — read at the call site to avoid module-level context coupling. */
  ledgerCredits: number
  /** Server callable that atomically debits Cr; omit to disable the LLM refine. */
  burnCredits?: AmbientLLMRefineDeps['burnCredits']
}

/**
 * Subscribes to the ambient signal bus, runs the evaluator whenever a fresh
 * signal arrives (debounced), and exposes the current top opportunity. Handles
 * dismissal, mute filtering, and the optional LLM refine call. Zero cost when
 * feature-flagged off — the effect early-returns.
 */
export const useAmbientOpportunity = (input: UseAmbientOpportunityInput) => {
  const [evaluatedOpportunity, setEvaluatedOpportunity] = useState<AmbientOpportunity | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const lastEvalAtRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)

  const inputRef = useRef(input)
  useEffect(() => {
    inputRef.current = input
  }, [input])

  const shouldEvaluate = lineFeatureFlags.ambient && !input.suppressed
  const current = shouldEvaluate ? evaluatedOpportunity : null

  useEffect(() => {
    if (!shouldEvaluate) {
      return
    }
    const evaluate = async () => {
      const now = Date.now()
      if (now - lastEvalAtRef.current < MIN_EVAL_INTERVAL_MS) return
      lastEvalAtRef.current = now
      const context: AmbientEvaluatorInput = {
        signals: ambientSignalBus.recent(now),
        workspaceId: inputRef.current.workspaceId,
        workspaceElements: inputRef.current.workspaceElements,
        checklistCompletionByOwner: inputRef.current.checklistCompletionByOwner,
        uploadsWithoutTimelineRef: inputRef.current.uploadsWithoutTimelineRef,
        now,
        dismissed: getAmbientDismissals(now),
        muted: getAmbientMutes(now),
      }
      const heuristics = evaluateAmbientOpportunities(context)
      if (heuristics[0]) {
        setEvaluatedOpportunity(heuristics[0])
        return
      }
      abortRef.current?.abort()
      abortRef.current = new AbortController()
      const refined = await refineOpportunitiesWithLLM(context, heuristics, {
        ledgerCredits: inputRef.current.ledgerCredits,
        burnCredits: inputRef.current.burnCredits,
        msSinceLastKeystroke: inputRef.current.msSinceLastKeystroke,
        signal: abortRef.current.signal,
      })
      setEvaluatedOpportunity(refined[0] ?? null)
    }
    const scheduleEvaluate = () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => { void evaluate() }, EVAL_DEBOUNCE_MS)
    }
    const unsubscribe = ambientSignalBus.subscribe(scheduleEvaluate)
    scheduleEvaluate()
    const interval = window.setInterval(scheduleEvaluate, 60_000)
    return () => {
      unsubscribe()
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      window.clearInterval(interval)
      abortRef.current?.abort()
    }
  }, [shouldEvaluate])

  const dismiss = useMemo(() => (opportunity: AmbientOpportunity) => {
    recordAmbientDismissal(opportunity)
    setEvaluatedOpportunity((existing) => (existing?.id === opportunity.id ? null : existing))
  }, [])

  const accept = useMemo(() => (opportunity: AmbientOpportunity) => {
    setEvaluatedOpportunity((existing) => (existing?.id === opportunity.id ? null : existing))
  }, [])

  return { current, dismiss, accept }
}
